import React, { useState, useEffect } from "react";
import { Power, Camera as CamIcon, Aperture, Zap, Download, Maximize2, Timer } from "lucide-react";
import { useApp } from "../../context/AppContext";
import { Panel, Overline } from "../common/Panel";
import { toast } from "sonner";

function Btn({ children, onClick, testid, variant = "default", active }) {
  const base = "inline-flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium border transition-colors active:scale-[0.98]";
  const v = variant === "danger" ? "border-[var(--danger)]/40 text-[var(--danger)] hover:bg-[var(--danger)]/10"
    : active ? "bg-[var(--accent)] text-black border-[var(--accent)]"
    : "bg-[var(--surface2)] border-[var(--bd)] hover:border-[var(--bd-strong)]";
  return <button data-testid={testid} onClick={onClick} className={`${base} ${v}`}>{children}</button>;
}

function Toggle({ on, onClick, testid }) {
  return <button data-testid={testid} onClick={onClick} className={`w-11 h-6 rounded-full relative transition-colors ${on ? "bg-[var(--accent)]" : "bg-[var(--track)]"}`}><span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${on ? "left-[22px]" : "left-0.5"}`} /></button>;
}

export default function CameraTab() {
  const { settings, updateSettings, addSnapshot, camImage, sendCamCommand, mqttConnected } = useApp();
  const [camOn, setCamOn] = useState(false);
  const [pirOn, setPirOn] = useState(false);
  const [autoSnap, setAutoSnap] = useState(false);
  const [autoInterval, setAutoInterval] = useState(10);
  const [fullscreen, setFullscreen] = useState(false);

  const handleCamOn = () => { sendCamCommand("cam:on"); setCamOn(true); toast.success("Camera ON sent"); };
  const handleCamOff = () => { sendCamCommand("cam:off"); setCamOn(false); toast.info("Camera OFF sent"); };
  const handleSnap = () => {
    if (settings.camAutoFlash) {
      sendCamCommand("led:on");
      setTimeout(() => { sendCamCommand("cam:snap"); }, 300);
      setTimeout(() => { sendCamCommand("led:off"); }, 1500);
      toast.info("⚡ Flash + snapshot");
    } else {
      sendCamCommand("cam:snap");
      toast.info("Snapshot requested");
    }
  };
  const handlePirToggle = () => {
    const next = !pirOn;
    sendCamCommand(next ? "pir:on" : "pir:off");
    setPirOn(next);
    toast.info(`PIR ${next ? "enabled" : "disabled"}`);
  };

  useEffect(() => {
    if (!autoSnap || !camOn) return;
    const id = setInterval(() => handleSnap(), autoInterval * 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [autoSnap, autoInterval, camOn]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <Overline>TTGO T-Camera (via MQTT)</Overline>
          <h2 className="font-heading font-bold text-2xl tracking-tight mt-1">Camera Management</h2>
        </div>
        <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-mono" style={{ color: mqttConnected ? "var(--accent)" : "var(--t2)", borderColor: mqttConnected ? "var(--accent)" : "var(--bd)" }} data-testid="camera-status">
          <span className="w-2 h-2 rounded-full" style={{ background: mqttConnected ? "var(--accent)" : "var(--t2)" }} />{mqttConnected ? "MQTT CONNECTED" : "MQTT OFFLINE"}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Panel className="p-6 space-y-5">
          <div className="flex flex-wrap gap-2">
            <Btn testid="cam-on" onClick={handleCamOn} active={camOn}><Power size={15} /> Camera ON</Btn>
            <Btn testid="cam-off" onClick={handleCamOff} variant="danger"><Power size={15} /> Camera OFF</Btn>
            <Btn testid="cam-snap" onClick={handleSnap}><Aperture size={15} /> Take Snapshot</Btn>
          </div>

          <div className="space-y-4 pt-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2"><Zap size={15} className="text-[var(--accent)]" /><span className="text-sm">Auto-flash on snapshot</span></div>
              <Toggle on={settings.camAutoFlash} testid="cam-autoflash-toggle" onClick={() => updateSettings({ camAutoFlash: !settings.camAutoFlash })} />
            </div>
            <div className="text-xs text-[var(--t2)] pl-7">GPIO 32 light hub fires before each snapshot as light source</div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2"><CamIcon size={15} className="text-[var(--accent)]" /><span className="text-sm">PIR motion snapshots</span></div>
              <Toggle on={pirOn} testid="cam-pir-toggle" onClick={handlePirToggle} />
            </div>
            <div className="text-xs text-[var(--t2)] pl-7">Auto-captures when TTGO PIR (GPIO 33) detects motion</div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2"><Timer size={15} className="text-[var(--accent)]" /><span className="text-sm">Auto snapshot interval</span></div>
              <Toggle on={autoSnap} testid="cam-auto-toggle" onClick={() => setAutoSnap((a) => !a)} />
            </div>
            {autoSnap && (
              <select data-testid="cam-auto-interval" className="w-full bg-[var(--surface2)] border border-[var(--bd)] rounded-md px-3 py-2 text-sm font-mono" value={autoInterval} onChange={(e) => setAutoInterval(parseInt(e.target.value))}>
                {[3, 5, 10, 30, 60].map((v) => <option key={v} value={v}>{v < 60 ? `${v}s` : "1 min"}</option>)}
              </select>
            )}
          </div>
        </Panel>

        <Panel className="p-6">
          <Overline>Last Snapshot</Overline>
          <div className="mt-3 aspect-video rounded-md bg-[var(--surface2)] border border-[var(--bd)] overflow-hidden flex items-center justify-center" data-testid="snapshot-viewer">
            {camImage ? (
              <img src={camImage} alt="TTGO camera snapshot" className="w-full h-full object-contain" data-testid="snapshot-image" />
            ) : (
              <div className="text-center text-[var(--t2)] p-8">
                <CamIcon size={40} className="mx-auto mb-3 opacity-40" />
                <p className="text-sm font-mono">No image from TTGO camera yet</p>
                <p className="text-xs mt-1">Turn camera ON, then click <strong>Take Snapshot</strong></p>
              </div>
            )}
          </div>
          {camImage && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-3">
              <a href={camImage} download={`ttgo-${Date.now()}.jpg`} data-testid="snapshot-download" className="py-2.5 rounded-md bg-[var(--surface2)] border border-[var(--bd)] text-sm flex items-center justify-center gap-2 hover:border-[var(--bd-strong)] transition-colors"><Download size={15} /> Download</a>
              <button onClick={() => { addSnapshot(camImage); toast.success("Saved to gallery"); }} className="py-2.5 rounded-md bg-[var(--surface2)] border border-[var(--bd)] text-sm flex items-center justify-center gap-2 hover:border-[var(--bd-strong)] transition-colors"><Download size={15} /> Save to Gallery</button>
              <button onClick={() => setFullscreen(true)} data-testid="snapshot-fullscreen" className="py-2.5 rounded-md bg-[var(--surface2)] border border-[var(--bd)] text-sm flex items-center justify-center gap-2 hover:border-[var(--bd-strong)] transition-colors"><Maximize2 size={15} /> Fullscreen</button>
            </div>
          )}
        </Panel>
      </div>

      {fullscreen && camImage && (
        <div className="fixed inset-0 z-[200] bg-black/95 flex items-center justify-center p-4" onClick={() => setFullscreen(false)}>
          <button onClick={() => setFullscreen(false)} className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white hover:bg-white/20 z-10">
            <Maximize2 size={18} />
          </button>
          <img src={camImage} alt="Fullscreen snapshot" className="max-w-full max-h-full object-contain rounded-lg" />
        </div>
      )}
    </div>
  );
}
